import {
  type UseQueryResult,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useScopedQueryKey } from '../../hooks/useScopedQueryKey';
import type { Bar, GateQuery } from '../../ui/v2';
import { api, apiEndpoints, getCurrentStoreId } from '../../utils/apiService';
import {
  type BillingPeriod,
  type DailyUsage,
  type GroupUsageSummary,
  INVOICES_PER_PAGE,
  type InvoicePage,
  type Plan,
  type PreviewChange,
  type Range,
  type Subscription,
  type UsageSummary,
  dailyRangeBounds,
  dayLabel,
  periodBarLabel,
  rangeCount,
} from './model';

/**
 * React Query wiring for Billing V2. One `BillingScope` drives every
 * endpoint so the store and group pages share hooks. Keys go through
 * `useScopedQueryKey` (user + store) plus the scope, so switching store or
 * group never shows another account's numbers.
 */
export type BillingScope =
  | { kind: 'store' }
  | { kind: 'group'; groupId: string };

const STALE = 60_000;
/** Every billing key starts with this, so a plan change can refresh them all. */
const KEY_PREFIX = 'billing-v2';

function scopeTag(scope: BillingScope) {
  return scope.kind === 'group' ? `group-${scope.groupId}` : 'store';
}

function useKey(
  scope: BillingScope,
  base: string,
  ...parts: Array<string | number | boolean | null | undefined>
) {
  const key = useScopedQueryKey(KEY_PREFIX, scopeTag(scope), base, ...parts);
  const serialized = JSON.stringify(key);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => key, [serialized]);
}

async function errorDetail(
  res: Response | null | undefined,
  fallback: string,
): Promise<string> {
  const body = res ? await res.json().catch(() => null) : null;
  return (body && typeof body.detail === 'string' && body.detail) || fallback;
}

async function getJson<T>(url: string, fallback: string): Promise<T> {
  const res = await api.get(url);
  if (!res || !res.ok) throw new Error(await errorDetail(res, fallback));
  return res.json();
}

const endpoints = {
  periods: (s: BillingScope) =>
    s.kind === 'group'
      ? apiEndpoints.getGroupBillingPeriods(s.groupId)
      : apiEndpoints.getBillingPeriods(),
  usage: (s: BillingScope, periodStart: string) =>
    `${
      s.kind === 'group'
        ? apiEndpoints.getGroupUsageSummary(s.groupId)
        : apiEndpoints.getUsageSummary()
    }?period_start=${encodeURIComponent(periodStart)}`,
  subscription: (s: BillingScope) =>
    s.kind === 'group'
      ? apiEndpoints.getGroupSubscription(s.groupId)
      : apiEndpoints.getBillingSubscription(),
  invoices: (s: BillingScope, page: number) =>
    `${
      s.kind === 'group'
        ? apiEndpoints.getGroupInvoices(s.groupId)
        : apiEndpoints.getBillingInvoices()
    }?page=${page}&page_size=${INVOICES_PER_PAGE}`,
  plans: (s: BillingScope) =>
    s.kind === 'group'
      ? apiEndpoints.getGroupPlans(s.groupId)
      : apiEndpoints.getPlans(),
  preview: (s: BillingScope, code: string) =>
    s.kind === 'group'
      ? apiEndpoints.getGroupSubscriptionPreviewChange(s.groupId, code)
      : `${apiEndpoints.getBillingSubscription()}preview-change/?plan_code=${encodeURIComponent(code)}`,
  portal: (s: BillingScope) =>
    s.kind === 'group'
      ? apiEndpoints.createGroupBillingPortalSession(s.groupId)
      : apiEndpoints.createBillingPortalSession(),
};

/** Queries run once auth is resolved and (store scope) a store is selected. */
export function useBillingEnabled(scope: BillingScope): boolean {
  const { user, loading } = useAuth();
  const storeId = getCurrentStoreId();
  return !loading && !!user && (scope.kind === 'group' || !!storeId);
}

export function toGate<T>(
  q: Pick<UseQueryResult<T>, 'status' | 'data' | 'error' | 'refetch'>,
): GateQuery<T> {
  return {
    status: q.status,
    data: q.data,
    error: q.error,
    refetch: () => void q.refetch(),
  };
}

export function useBillingPeriods(scope: BillingScope) {
  const enabled = useBillingEnabled(scope);
  return useQuery<BillingPeriod[]>({
    queryKey: useKey(scope, 'periods'),
    queryFn: () =>
      getJson(endpoints.periods(scope), 'Billing periods could not be loaded.'),
    enabled,
    staleTime: STALE,
  });
}

function usageQueryOptions<T>(
  scope: BillingScope,
  key: unknown[],
  periodStart: string,
) {
  return {
    queryKey: key,
    queryFn: () =>
      getJson<T>(
        endpoints.usage(scope, periodStart),
        scope.kind === 'group'
          ? 'Group usage could not be loaded.'
          : 'Usage for this period could not be loaded.',
      ),
    staleTime: STALE,
  };
}

export function useUsageSummary<
  T extends UsageSummary | GroupUsageSummary = UsageSummary,
>(scope: BillingScope, period: BillingPeriod | undefined) {
  const enabled = useBillingEnabled(scope) && !!period;
  const key = useKey(scope, 'usage', period?.period_start);
  return useQuery<T>({
    ...usageQueryOptions<T>(scope, key, period?.period_start ?? ''),
    enabled,
  });
}

/** 404 → `null`: no subscription yet. */
export function useSubscription(scope: BillingScope, enabled = true) {
  const base = useBillingEnabled(scope);
  return useQuery<Subscription | null>({
    queryKey: useKey(scope, 'subscription'),
    queryFn: async () => {
      const res = await api.get(endpoints.subscription(scope));
      if (res && res.status === 404) return null;
      if (!res || !res.ok)
        throw new Error(
          await errorDetail(res, 'The subscription could not be loaded.'),
        );
      return res.json();
    },
    enabled: base && enabled,
    staleTime: STALE,
  });
}

/**
 * Bars for the "Usage over time" range. Week / period read the store's
 * daily endpoint; 6 / 12 / all read one usage summary per period, through
 * the same cache entries the period view uses.
 */
export function useUsageHistory(
  scope: BillingScope,
  periods: BillingPeriod[],
  period: BillingPeriod | undefined,
  range: Range,
  tz: string | undefined,
) {
  const queryClient = useQueryClient();
  const enabled = useBillingEnabled(scope) && !!period && !!tz;
  const usageBase = useKey(scope, 'usage');
  return useQuery<Bar[]>({
    queryKey: useKey(
      scope,
      'history',
      period?.period_start,
      range,
      periods.length,
      tz,
    ),
    queryFn: async () => {
      if (!period || !tz) return [];
      if (scope.kind === 'store' && (range === 'week' || range === 'period')) {
        const { start_date, end_date } = dailyRangeBounds(period, range, tz);
        const rows = await getJson<DailyUsage[]>(
          `${apiEndpoints.getUsageSummary()}daily/?start_date=${start_date}&end_date=${end_date}`,
          'Usage history could not be loaded.',
        );
        return rows.map((d) => ({
          key: d.date,
          label: dayLabel(d.date),
          value: d.minutes_used,
        }));
      }
      const from = Math.max(
        0,
        periods.findIndex((p) => p.period_start === period.period_start),
      );
      const slice = periods
        .slice(from, from + rangeCount(range, periods.length))
        .reverse();
      const summaries = await Promise.all(
        slice.map((p) =>
          queryClient.fetchQuery(
            usageQueryOptions<UsageSummary | GroupUsageSummary>(
              scope,
              [...usageBase, p.period_start],
              p.period_start,
            ),
          ),
        ),
      );
      return slice.map((p, i) => {
        const s = summaries[i];
        return {
          key: p.period_start,
          label: periodBarLabel(p, tz),
          value:
            'total_minutes_used' in s ? s.total_minutes_used : s.minutes_used,
          selected: p.period_start === period.period_start,
        };
      });
    },
    enabled,
    staleTime: STALE,
  });
}

export function useInvoices(scope: BillingScope, page: number, enabled = true) {
  const base = useBillingEnabled(scope);
  return useQuery<InvoicePage>({
    queryKey: useKey(scope, 'invoices', page),
    queryFn: async () => {
      const body = await getJson<Partial<InvoicePage>>(
        endpoints.invoices(scope, page),
        'Invoices could not be loaded.',
      );
      return { count: body.count ?? 0, results: body.results ?? [] };
    },
    enabled: base && enabled,
    staleTime: STALE,
  });
}

export function usePlans(scope: BillingScope, enabled = true) {
  const base = useBillingEnabled(scope);
  return useQuery<Plan[]>({
    queryKey: useKey(scope, 'plans'),
    queryFn: () =>
      getJson(endpoints.plans(scope), 'Plans could not be loaded.'),
    enabled: base && enabled,
    staleTime: STALE,
  });
}

/** Drop every cached billing read so the next page shows the new plan. */
export function useInvalidateBilling() {
  const queryClient = useQueryClient();
  return useCallback(
    () =>
      queryClient.invalidateQueries({
        predicate: (q) => q.queryKey[0] === KEY_PREFIX,
      }),
    [queryClient],
  );
}

export async function loadPreview(
  scope: BillingScope,
  planCode: string,
): Promise<PreviewChange> {
  return getJson(
    endpoints.preview(scope, planCode),
    'The price for this change could not be loaded.',
  );
}

/**
 * POST the plan change. A live subscription changes in place (200, no
 * body we read); otherwise the server answers with a Stripe Checkout URL.
 */
export async function submitPlan(
  scope: BillingScope,
  body: Record<string, unknown>,
): Promise<{ checkout_url?: string }> {
  const res = await api.post(endpoints.subscription(scope), body);
  if (!res || !res.ok)
    throw new Error(
      await errorDetail(res, 'The plan could not be changed. Try again.'),
    );
  return res.json().catch(() => ({}));
}

/**
 * Stripe customer portal hand-off. The busy state resets on `pageshow` —
 * Back from Stripe restores this page from the bfcache with stale state.
 */
export function usePortal(scope: BillingScope) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const onShow = () => setBusy(false);
    window.addEventListener('pageshow', onShow);
    return () => window.removeEventListener('pageshow', onShow);
  }, []);
  const open = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post(endpoints.portal(scope));
      if (!res || !res.ok)
        throw new Error(
          await errorDetail(res, 'The Stripe portal could not be opened.'),
        );
      const body = await res.json();
      if (!body.portal_url)
        throw new Error('The Stripe portal could not be opened.');
      window.location.href = body.portal_url;
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  }, [scope]);
  return { busy, error, open };
}
